import React, { useState } from "react";
import Footer from "./Footer";
import "../css/contactus.scss";
import Header from "./Header";

function Contact() {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [errors, setErrors] = useState({});

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // 清除该字段的错误信息
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  // 验证邮箱格式
  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // 验证所有字段并返回错误信息
  const validateForm = () => {
    const newErrors = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = "First name is required";
    }
    if (!formData.lastName.trim()) {
      newErrors.lastName = "Last name is required";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!isValidEmail(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    if (!formData.subject) {
      newErrors.subject = "Please select a subject";
    }

    if (!formData.message.trim()) {
      newErrors.message = "Message is required";
    } else if (formData.message.trim().length < 10) {
      newErrors.message = "Message must be at least 10 characters";
    }

    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // 验证表单
    const formErrors = validateForm();

    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
      // 滚动到第一个错误字段
      const firstErrorField = Object.keys(formErrors)[0];
      const element = document.getElementById(firstErrorField);
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "center" });
        element.focus();
      }
      return;
    }

    setIsSubmitting(true);
    setErrors({});

    // 模拟表单提交
    setTimeout(() => {
      setShowSuccess(true);
      setFormData({
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        subject: "",
        message: "",
      });
      setIsSubmitting(false);

      // 滚动到顶部显示成功消息
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });

      // 5秒后隐藏成功消息
      setTimeout(() => {
        setShowSuccess(false);
      }, 5000);
    }, 1000);
  };

  return (
    <div className="contact">
      <Header />

      <main className="contact-main">
        {showSuccess && (
          <div className="contact-success">
            <div className="success-title">
              Thanks — your message has been sent.
            </div>
            <div className="success-sub">
              Our team will get back to you soon.
            </div>
          </div>
        )}

        <section className="contact-hero">
          <div className="hero-left">
            <h1 className="hero-title">We’re here to help.</h1>
            <p className="hero-sub">
              Find quick answers for common questions or reach out to our
              support team any time.
            </p>
            <div className="hero-callout">
              <div className="callout-title">Call Us</div>
              <div className="callout-sub">Hours: 24/7</div>
            </div>
          </div>
          <div className="hero-right">
            <ul className="quick-list">
              <li className="quick-item">
                <div className="quick-title">
                  Reset or power cycle your device
                </div>
                <div className="quick-sub">
                  Learn how to factory reset or power cycle doorbells, cameras,
                  and Chimes.
                </div>
              </li>
              <li className="quick-item">
                <div className="quick-title">Checking Device Health</div>
                <div className="quick-sub">
                  Troubleshoot notification, power and Wi‑Fi issues from Device
                  Health.
                </div>
              </li>
              <li className="quick-item">
                <div className="quick-title">Managing permissions</div>
                <div className="quick-sub">
                  Share limited access with a friend or family member.
                </div>
              </li>
              <li className="quick-item">
                <div className="quick-title">Update the Rivergrove app</div>
                <div className="quick-sub">
                  Download and update to the latest version on your phone.
                </div>
              </li>
            </ul>
          </div>
        </section>

        <section className="contact-form">
          <h2 className="form-title">Send us a message</h2>
          <form onSubmit={handleSubmit} noValidate>
            <div className="form-row">
              <div className="form-field">
                <label htmlFor="firstName">First name</label>
                <input
                  id="firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  aria-invalid={!!errors.firstName}
                />
                {errors.firstName && (
                  <div className="field-error">{errors.firstName}</div>
                )}
              </div>
              <div className="form-field">
                <label htmlFor="lastName">Last name</label>
                <input
                  id="lastName"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  aria-invalid={!!errors.lastName}
                />
                {errors.lastName && (
                  <div className="field-error">{errors.lastName}</div>
                )}
              </div>
            </div>

            <div className="form-row">
              <div className="form-field">
                <label htmlFor="email">Email</label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  aria-invalid={!!errors.email}
                />
                {errors.email && (
                  <div className="field-error">{errors.email}</div>
                )}
              </div>
              <div className="form-field">
                <label htmlFor="phone">Phone (optional)</label>
                <input
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-field">
                <label htmlFor="subject">Subject</label>
                <select
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleInputChange}
                  aria-invalid={!!errors.subject}
                >
                  <option value="">Select an issue</option>
                  <option value="account">Account & Billing</option>
                  <option value="device">Device Setup</option>
                  <option value="connectivity">Connectivity</option>
                  <option value="privacy">Privacy & Security</option>
                  <option value="other">Other</option>
                </select>
                {errors.subject && (
                  <div className="field-error">{errors.subject}</div>
                )}
              </div>
            </div>

            <div className="form-row">
              <div className="form-field full">
                <label htmlFor="message">Message</label>
                <textarea
                  id="message"
                  name="message"
                  rows={6}
                  value={formData.message}
                  onChange={handleInputChange}
                  aria-invalid={!!errors.message}
                />
                {errors.message && (
                  <div className="field-error">{errors.message}</div>
                )}
              </div>
            </div>

            <div className="form-actions">
              <button
                className="btn primary"
                type="submit"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Sending..." : "Submit"}
              </button>
            </div>
          </form>
        </section>
      </main>

      <Footer />
    </div>
  );
}

export default Contact;
